import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { EDataView } from '@/config/globals';
import type { BlogPostFieldsFragment } from '@/graphql/gql/graphql';
import type { OmitFunctions } from '@/types/';
import { qsStorage } from '@/utils';
import { createSelectors } from './selector';

interface BlogSlice {
  page: number;
  search: string;
  view: EDataView;
  /** Tag names sent to `blogPosts(tags: …)` */
  tagNames: string[];

  /** Full-screen create/edit modal */
  editorOpen: boolean;
  /** `null` = create; post = edit (data from list/grid) */
  editorPost: BlogPostFieldsFragment | null;
  /** Tab shown when the editor opens (title click → preview; default edit). */
  editorInitialTab: 'edit' | 'preview';

  setSearch: (search: string) => void;
  setTagNames: (tagNames: string[]) => void;
  setView: (view: EDataView) => void;
  setPage: (page: number) => void;
  resetFilters: () => void;
  openBlogEditor: (
    post: BlogPostFieldsFragment | null,
    options?: { initialTab?: 'edit' | 'preview' },
  ) => void;
  closeBlogEditor: () => void;
}

const initialState: OmitFunctions<BlogSlice> = {
  page: 1,
  search: '',
  view: EDataView.GRID,
  tagNames: [],
  editorOpen: false,
  editorPost: null,
  editorInitialTab: 'edit',
};

const blogSlice = create<BlogSlice>()(
  persist(
    (set) => ({
      ...initialState,
      setSearch: (search) => {
        set({ search, page: 1 });
      },
      setTagNames: (tagNames) => {
        set({ tagNames, page: 1 });
      },
      setView: (view) => {
        set({ view });
      },
      setPage: (page) => {
        set({ page });
      },
      resetFilters: () => {
        set({
          search: initialState.search,
          tagNames: initialState.tagNames,
          page: initialState.page,
        });
      },
      openBlogEditor: (post, options) => {
        set({
          editorOpen: true,
          editorPost: post,
          editorInitialTab: options?.initialTab ?? 'edit',
        });
      },
      closeBlogEditor: () => {
        set({ editorOpen: false, editorPost: null, editorInitialTab: 'edit' });
      },
    }),
    {
      name: 'blog-filters',
      storage: createJSONStorage(() => qsStorage),
      partialize: (state) => ({
        search: state.search,
        view: state.view,
        page: state.page,
        tagNames: state.tagNames,
      }),
    },
  ),
);

export const useBlog = createSelectors(blogSlice);
